import React, { useCallback, useRef, useState } from 'react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import { CBRetentionLogo } from '@bbkAdminComponents/cbretention-logo';
import Icon from '@bbkAdminComponents/icons/icon-generator';
import { useInfoModal } from '@bbkAdminComponents/info-dialog';
import { Label } from '@bbkAdminComponents/inputs/label/label';
import { PasswordInput } from '@bbkAdminComponents/inputs/password/password-input';
import { LogInError } from '@bbkAdminComponents/log-in-flow/error';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import type * as LogInTypes from '@bbkAdminComponents/log-in-flow/log-in-types';
import { SigninButton } from '@bbkAdminComponents/log-in-flow/signin-button';
import { LogInSuccess } from '@bbkAdminComponents/log-in-flow/success';
import { LogInTrouble } from '@bbkAdminComponents/log-in-flow/trouble';
import {
  preauthCompanySlice,
  type CheckUsernameResponse,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { Icons } from '@bbkAdminUtils/icons';

type SignInTypes = {
  error?: LogInTypes.Error;
  success?: LogInTypes.Success;
  formValues: LogInTypes.FormValues;
  googleSignIn: LogInTypes.GoogleSignIn;
  chargebeeSignIn: LogInTypes.ChargebeeSignIn;
  signIn: LogInTypes.SignIn;
  sending: LogInTypes.Sending;
  isCBPath?: boolean;
};
export const SignIn: React.FC<SignInTypes> = ({
  formValues,
  googleSignIn,
  signIn,
  chargebeeSignIn,
  error,
  sending,
  success,
  isCBPath,
}) => {
  const { email, password, setEmail, setPassword } = formValues;

  const pwdRef = useRef<HTMLInputElement>(null);
  const { triggerModal } = useInfoModal();

  const isPublicUsernamePasswordDisabled = useFeatureFlags(
    FeatureFlagsE.DISABLE_PUBLIC_USERNAME_PASSWORD_AUTH
  );
  const isDevLoginAuthEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_DEV_LOGIN_AUTH
  );
  const isCbIdpPhase4Enabled = useFeatureFlags(
    FeatureFlagsE.CHARGEBEE_IDP_PHASE_4
  );
  const isPasswordAuthAvailable =
    isDevLoginAuthEnabled || !isPublicUsernamePasswordDisabled;
  const showGoogleSignIn = !isCbIdpPhase4Enabled;
  const [showPasswordField, setShowPasswordField] = useState<boolean>(
    isPasswordAuthAvailable && !!error
  );

  const [cbidpCheckUsername$] =
    preauthCompanySlice.endpoints.cbidpCheckUsername.useLazyQuery();
  const [cbidpInitiateMigration$] =
    preauthCompanySlice.endpoints.cbidpInitiateMigration.useLazyQuery();
  const checkUsername = useCallback(
    async (em: string) => {
      if (!em?.trim()) return;

      let isEmailAnCBIDPUser = false;
      let cbLoginBaseUrl;
      let showMigration = true;
      try {
        const response: CheckUsernameResponse = await cbidpCheckUsername$(
          { email: em },
          true
        ).unwrap();
        isEmailAnCBIDPUser = response.valid;
        cbLoginBaseUrl = response.url;
        showMigration = response?.code !== 'not_found';
      } catch (e) {
        BlackToastTrigger({
          content: 'Could not check email. Please try again',
        });
        GlobalLogger.error(e);
      } finally {
        setShowPasswordField(isPasswordAuthAvailable && !isEmailAnCBIDPUser);
        if (!isEmailAnCBIDPUser) {
          if (!isPasswordAuthAvailable && showGoogleSignIn) {
            BlackToastTrigger({
              content: 'Continue with Google or Chargebee to sign in.',
            });
          } else if (isCbIdpPhase4Enabled && showMigration) {
            setShowPasswordField(!isCbIdpPhase4Enabled);
            cbidpInitiateMigration$({ email: em })
              .unwrap()
              .then(() => {
                triggerModal({
                  title: <>Login system update</>,
                  description: (
                    <>
                      We are updating our login system to make it easier for our
                      customers to access all Chargebee products. Please check
                      your email for instructions to set up your credentials.
                    </>
                  ),
                });
              })
              .catch((e: RTKAPIErrorResponse) => {
                const err = parseRTKAPIError(e);
                BlackToastTrigger({
                  content:
                    'Failed to Initiate Account Migration. Please check your email.',
                });
                GlobalLogger.error(
                  `cbidpInitiateMigration error: ${err?.message}`
                );
              });
          } else {
            pwdRef.current?.focus();
          }
        } else {
          chargebeeSignIn(email, cbLoginBaseUrl);
        }
      }
    },
    [
      cbidpCheckUsername$,
      chargebeeSignIn,
      cbidpInitiateMigration$,
      isCbIdpPhase4Enabled,
      isPasswordAuthAvailable,
      email,
      showGoogleSignIn,
      triggerModal,
    ]
  );

  return (
    <div>
      <div className="content">
        <CBRetentionLogo className="tw-mb-6 tw-h-10" />
        <div className="tw-mb-4 tw-font-semibold">Sign-in to continue</div>
        {success && <LogInSuccess success={success} />}
        {error && <LogInError error={error} />}
        <form
          target="#"
          onSubmit={
            isPasswordAuthAvailable && showPasswordField
              ? signIn
              : (e) => e.preventDefault()
          }
          data-testid="sign-in-form"
        >
          <div className="form-container">
            <Label id="email" className="tw-text-sm tw-font-semibold">
              Email
            </Label>
            <input
              type="email"
              name="email"
              className="bbk-simple-text-input medium"
              autoComplete="on"
              placeholder="Enter your email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => {
                setShowPasswordField(false);
              }}
              disabled={sending || isCBPath}
            />
            {isPasswordAuthAvailable && showPasswordField && (
              <div>
                <Label
                  id="password"
                  helpText="12 character minimum"
                  className="tw-text-sm tw-font-semibold"
                >
                  Password
                </Label>
                <PasswordInput
                  ref={pwdRef}
                  name="password"
                  autoComplete="on"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={sending}
                />
              </div>
            )}
            {isPasswordAuthAvailable && showPasswordField ? (
              <ButtonV2
                onClick={signIn}
                disabled={sending}
                primary
                className="submitButtonFooter tw-w-full"
              >
                Sign in <Icon name="arrow" class="tw-inline" />
              </ButtonV2>
            ) : (
              <ButtonV2
                disabled={sending}
                primary
                className="submitButtonFooter tw-w-full"
                onClick={() => {
                  void checkUsername(email);
                }}
              >
                Continue
              </ButtonV2>
            )}
            {showGoogleSignIn && (
              <>
                <div className="signin-divider !tw-mb-2">
                  <div className="or">or</div>
                </div>
                <SigninButton
                  copy="Sign in with Google"
                  onClick={googleSignIn}
                  icon={Icons.google}
                />
              </>
            )}
            <button type="submit" className="tw-hidden">
              Sign in
            </button>
          </div>
        </form>
      </div>
      {!isPublicUsernamePasswordDisabled && !isDevLoginAuthEnabled && (
        <LogInTrouble
          link={{ path: LogInPaths.forgotPassword, copy: 'Forgot password?' }}
        />
      )}
    </div>
  );
};
